import { create } from 'zustand';
import type { VisibilityState } from '@tanstack/react-table';

/**
 * Per-dataset column visibility for the dashboard data table.
 *
 * Lives in a separate store (rather than in `DataTable` local state) so the
 * Excel export can read the user's hidden-column choices and project them
 * out of sheet 1. Not persisted to IndexedDB — visibility is session state.
 */
type State = {
  visibility: Record<string, VisibilityState>;
  setVisibility: (datasetId: string, vis: VisibilityState) => void;
};

export const useTableVisibilityStore = create<State>((set) => ({
  visibility: {},
  setVisibility: (datasetId, vis) =>
    set((s) => ({
      visibility: { ...s.visibility, [datasetId]: vis },
    })),
}));

/**
 * Resolve the list of columns that are *hidden* for `datasetId`. A column
 * is hidden when its visibility flag is explicitly `false`; missing keys
 * default to visible (TanStack Table semantics).
 */
export function getHiddenColumns(
  datasetId: string,
  allColumns: string[],
): string[] {
  const vis = useTableVisibilityStore.getState().visibility[datasetId];
  if (!vis) return [];
  return allColumns.filter((c) => vis[c] === false);
}
